import { Box, lighten } from '@mui/material'
import { FeatureFlagIdNewNav2026 } from 'c1gen/features'
import { appBarHeight, sidebarCollapsedWidth, sidebarWidth } from 'c1ui/theme/C1Spacing'
import { palette } from 'c1ui/theme/palette'
import { navigationConfig } from 'components/modular-nav'
import { navSidebarOverlayWidth, navSidebarWidthCollapsed, navSidebarWidthExpanded } from 'components/modular-nav/types'
import { useFeatureChecker } from 'components/no-ui/capability'
import { COLLAPSED_SIDEBAR_NAV_STORAGE_KEY, getTransition } from 'components/pages/sidebar/CollapsingSidebar'
import { useLocalStorageContext } from 'hooks/local-storage-context'
import { AdminModeContext, ScrollContainerContext } from 'lib/context/app'
import { useRouter } from 'next/router'
import { ReactNode, useContext, useMemo, useRef } from 'react'

type ScrollContainerProviderProps = {
    children: ReactNode
    noStylePage?: boolean
}

/**
 * This context exists to hold a ref to the scrolling container, in case
 * child components need to bind a scroll handler.
 */

export const ScrollContainerProvider = ({ children, noStylePage }: ScrollContainerProviderProps): React.ReactElement => {
    const [isCollapsed] = useLocalStorageContext(COLLAPSED_SIDEBAR_NAV_STORAGE_KEY, false)
    const adminModeContext = useContext(AdminModeContext)
    const hasNewNav = useFeatureChecker(FeatureFlagIdNewNav2026)
    const ref = useRef<HTMLDivElement>(null)
    const value = useMemo(() => ({ ref }), [ref])

    const router = useRouter()
    const pathname = router.pathname

    // Check if current route has a persistent flyout (applies whether collapsed or not)
    const hasPersistentFlyout = useMemo(() => {
        if (!hasNewNav) return false
        return navigationConfig.some((container) =>
            container.persistentPaths?.some((path) =>
                pathname.startsWith(path) || router.asPath.startsWith(path)))
    }, [hasNewNav, pathname, router.asPath])

    // Calculate margin-left based on nav mode
    const getMarginLeft = (): string => {
        if (hasNewNav) {
            // New nav always shows sidebar, just collapsed or expanded
            const sidebarMargin = isCollapsed ? navSidebarWidthCollapsed : navSidebarWidthExpanded
            if (hasPersistentFlyout) {
                return `calc(${sidebarMargin} + ${navSidebarOverlayWidth})`
            }
            return sidebarMargin
        }
        // Legacy nav only shows sidebar on admin paths with admin role
        if (!(adminModeContext.isOnAdminPath && adminModeContext.hasAdminRole)) {
            return '0'
        }
        return isCollapsed ? sidebarCollapsedWidth : sidebarWidth
    }

    return <ScrollContainerContext.Provider value={value}>
        <Box
            component="main"
            id="main-content"
            sx={noStylePage ? {
                height: '100vh',
            } : {
                marginTop: hasNewNav ? 0 : appBarHeight,
                marginLeft: getMarginLeft(),
                backgroundColor: pathname === '/'
                    ? lighten(palette.primary.extraLight, 0.6)
                    : 'grey.50',
                height: hasNewNav ? '100vh' : `calc(100vh - ${appBarHeight})`,
                overflow: 'auto',
                overflowX: 'hidden',
                ...getTransition('margin-left'),
            }}
            ref={ref}
        >
            {children}
        </Box>
    </ScrollContainerContext.Provider>
}
